// Récompenses du partage (remplacent les pièces et la boutique en version 1). Logique pure, sans DOM ni stockage.
//   - Partager la carte d'un kami qu'on n'avait pas encore partagé donne UNE charge rapide (consommée à l'usage). On n'en garde qu'une à la
//     fois (MAX_FAST_CHARGES) : tant qu'elle n'est pas utilisée, un nouveau kami partagé n'en ajoute pas (il compte quand même pour la collection).
//   - Partager tous les kamis de sa galerie (la collection : ceux qu'on a photographiés, au moins deux) donne UNE batterie longue durée ;
//     le suivi des partages repart alors de zéro, pour en gagner une autre. Avec moins de deux kamis en galerie, il n'y a pas de collection.
// État : { shared: number[] (kamis partagés dans la collection en cours), fastCharges: number, longLifeRights: number }
// Tout ce qui vient du stockage est revalidé par `sanitizeRewards`.

/**
 * Version iOS (simulée par un réglage) : une charge rapide est offerte à chaque niveau, une seule fois ; ensuite il faut partager la carte d'un kami.
 * @param {boolean} ios  la version iOS est active
 * @param {number} level  le niveau en cours
 * @param {number} usedLevel  le niveau où la charge offerte a été utilisée (0 = aucune)
 */
export function iosFreeFastAvailable(ios, level, usedLevel) {
  return !!ios && usedLevel !== level;
}

/** Niveau mémorisé de la charge offerte, revalidé (entier positif, sinon 0). */
export function cleanFreeFastLevel(value) {
  return Number.isInteger(value) && value > 0 ? value : 0;
}

/** Charges rapides gardées en même temps. */
export const MAX_FAST_CHARGES = 1;

export const EMPTY_REWARDS = Object.freeze({ shared: Object.freeze([]), fastCharges: 0, longLifeRights: 0 });

const count = (value) => (Number.isInteger(value) && value >= 0 ? value : 0);

/**
 * @param {unknown} raw  valeur lue du stockage (n'importe quoi)
 * @param {number} total  nombre de kamis de la famille
 */
export function sanitizeRewards(raw, total) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_REWARDS, shared: [] };
  const shared = Array.isArray(raw.shared)
    ? [...new Set(raw.shared.filter((id) => Number.isInteger(id) && id >= 0 && id < total))].sort((a, b) => a - b)
    : [];
  return { shared, fastCharges: Math.min(MAX_FAST_CHARGES, count(raw.fastCharges)), longLifeRights: count(raw.longLifeRights) };
}

/** Nombre minimal de kamis en galerie pour qu'il y ait une collection à partager en entier (donc une batterie longue durée à gagner). */
export const MIN_COLLECTION = 2;

const idsOf = (indices, total) => [...new Set(indices.map((index) => index % total))];

/** Les kamis de la collection (galerie) déjà partagés : la progression « n / m » de la batterie longue durée. */
export function collectionProgress(rewards, collection, total) {
  const ids = idsOf(collection, total);
  return { shared: ids.filter((id) => rewards.shared.includes(id)).length, total: ids.length };
}

/**
 * Un kami vient d'être partagé.
 * @param {ReturnType<typeof sanitizeRewards>} rewards
 * @param {number} kamiIndex  index dans la famille (au-delà de la liste : le kami du même nom, voir data/family.js)
 * @param {number} total  nombre de kamis de la famille
 * @param {number[]} [collection]  les kamis de la galerie du joueur (indices) : tous partagés = une batterie longue durée
 * @returns {{ rewards: ReturnType<typeof sanitizeRewards>, fastGained: boolean, longLifeGained: boolean }}
 */
export function recordShare(rewards, kamiIndex, total, collection = []) {
  const id = kamiIndex % total;
  const isNew = !rewards.shared.includes(id);
  const shared = isNew ? [...rewards.shared, id].sort((a, b) => a - b) : rewards.shared;
  const gainsFast = isNew && rewards.fastCharges < MAX_FAST_CHARGES; // une seule charge rapide à la fois
  const ids = idsOf(collection, total);
  const complete = ids.length >= MIN_COLLECTION && ids.every((one) => shared.includes(one));
  if (complete) {
    return { rewards: { shared: [], fastCharges: rewards.fastCharges + (gainsFast ? 1 : 0), longLifeRights: rewards.longLifeRights + 1 }, fastGained: gainsFast, longLifeGained: true };
  }
  if (!isNew) return { rewards, fastGained: false, longLifeGained: false };
  return { rewards: { ...rewards, shared, fastCharges: rewards.fastCharges + (gainsFast ? 1 : 0) }, fastGained: gainsFast, longLifeGained: false };
}

/** Nouvelle partie (galerie vidée) : les kamis déjà partagés ne comptent plus ; les récompenses gagnées sont gardées. */
export function resetShared(rewards) {
  return { ...rewards, shared: [] };
}

/** Utilise une charge rapide ; null s'il n'y en a pas. */
export function spendFastCharge(rewards) {
  return rewards.fastCharges > 0 ? { ...rewards, fastCharges: rewards.fastCharges - 1 } : null;
}

/** Utilise une batterie longue durée ; null s'il n'y en a pas. */
export function spendLongLife(rewards) {
  return rewards.longLifeRights > 0 ? { ...rewards, longLifeRights: rewards.longLifeRights - 1 } : null;
}
